import type {
  AgentTodoItem,
  AgentTodoItemStatus,
  AgentTodoOperation,
  ParsedAgentTodoResult,
} from './types';

const TODO_OPERATIONS = new Set<AgentTodoOperation>([
  'set',
  'create',
  'get',
  'update',
  'complete',
  'delete',
]);

const TODO_STATUSES = new Set<AgentTodoItemStatus>([
  'pending',
  'in_progress',
  'completed',
  'cancelled',
]);

const ITEM_TAG_RE =
  /<item\b([^>]*)>\s*<title>([\s\S]*?)<\/title>(?:\s*<notes>([\s\S]*?)<\/notes>)?\s*<\/item>/gi;

const decodeXmlText = (raw: string): string =>
  raw
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&amp;/g, '&')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .trim();

const readAttr = (attrs: string, name: string): string | undefined => {
  const match = attrs.match(new RegExp(`\\b${name}="([^"]*)"`));
  return match?.[1];
};

const parseStatus = (raw: string | undefined): AgentTodoItemStatus | null => {
  if (!raw || !TODO_STATUSES.has(raw as AgentTodoItemStatus)) return null;
  return raw as AgentTodoItemStatus;
};

const parseItemsFromXml = (xml: string): AgentTodoItem[] => {
  const items: AgentTodoItem[] = [];
  for (const match of xml.matchAll(ITEM_TAG_RE)) {
    const attrs = match[1] ?? '';
    const title = decodeXmlText(match[2] ?? '');
    const notesRaw = match[3];
    const id = Number(readAttr(attrs, 'id'));
    const status = parseStatus(readAttr(attrs, 'status'));
    if (!Number.isFinite(id) || !title || !status) continue;
    items.push({
      id,
      title,
      status,
      ...(notesRaw ? { notes: decodeXmlText(notesRaw) } : {}),
    });
  }
  return items;
};

/**
 * Parse the harness todo tool's `result.content` XML (and any trailing banner
 * text) into structured items for the transcript checklist.
 */
export const parseAgentTodoResult = (
  content: string | undefined
): ParsedAgentTodoResult | null => {
  if (!content?.trim()) return null;

  const taskListMatch = content.match(/<task_list>[\s\S]*<\/task_list>/i);
  const singleItemMatch = content.match(/<item\b[\s\S]*<\/item>/i);

  if (taskListMatch) {
    const items = parseItemsFromXml(taskListMatch[0]);
    const banner = content
      .slice(taskListMatch.index! + taskListMatch[0].length)
      .trim()
      .replace(/^\n+/, '');
    return {
      items,
      ...(banner ? { banner } : {}),
    };
  }

  if (singleItemMatch) {
    const items = parseItemsFromXml(singleItemMatch[0]);
    return items.length > 0 ? { items } : null;
  }

  return { items: [], banner: content.trim() };
};

export const readTodoOperation = (
  input: Record<string, unknown>
): AgentTodoOperation | null => {
  const operation = input.operation;
  return typeof operation === 'string' &&
    TODO_OPERATIONS.has(operation as AgentTodoOperation)
    ? (operation as AgentTodoOperation)
    : null;
};

/** How many todos this call touches — drives singular/plural copy while running. */
export const countTodosInInput = (
  operation: AgentTodoOperation,
  input: Record<string, unknown>
): number => {
  switch (operation) {
    case 'create':
    case 'set':
      return Array.isArray(input.items) ? input.items.length : 0;
    case 'update':
      return Array.isArray(input.updates) ? input.updates.length : 0;
    case 'complete':
    case 'delete':
      return Array.isArray(input.ids) ? input.ids.length : 0;
    case 'get':
      return typeof input.id === 'number' ? 1 : 0;
  }
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const readString = (record: Record<string, unknown>, key: string): string | undefined => {
  const value = record[key];
  return typeof value === 'string' && value.trim() ? value.trim() : undefined;
};

/** Best-effort titles for complete/update/delete subtitles from the input payload. */
export const readAffectedTodoTitles = (
  operation: AgentTodoOperation,
  input: Record<string, unknown>,
  items: AgentTodoItem[]
): string[] => {
  const byId = new Map(items.map((item) => [item.id, item.title]));

  if (operation === 'complete' || operation === 'delete') {
    const ids = Array.isArray(input.ids)
      ? input.ids.filter((id): id is number => typeof id === 'number')
      : [];
    return ids.map((id) => byId.get(id) ?? `#${id}`);
  }

  if (operation === 'update') {
    const updates = Array.isArray(input.updates) ? input.updates : [];
    return updates
      .filter(isRecord)
      .map((update) => {
        const id = typeof update.id === 'number' ? update.id : null;
        const title = readString(update, 'title');
        if (title) return title;
        return id !== null ? (byId.get(id) ?? `#${id}`) : null;
      })
      .filter((title): title is string => !!title);
  }

  if (operation === 'create' || operation === 'set') {
    const rawItems = Array.isArray(input.items) ? input.items : [];
    return rawItems
      .filter(isRecord)
      .map((item) => readString(item, 'title'))
      .filter((title): title is string => !!title);
  }

  return [];
};
